/**
 * My Brand aspect-ratio tokens for `core.raw_table_view`.`aspect_ratio`.
 *
 * BigQuery stores the named ratios with a semicolon (`16;9`) and the rest as
 * lowercase `other`; the workspace SQL layer normalizes before comparing.
 * `Other` means any Google row outside the named ratios, including NULL and blank.
 * Empty or every token means All. The Dashboard competitor aspect-ratio mix
 * buckets competitor `Topic_Title` tags into the same five tokens.
 * Product copy lives in the frontend i18n files — this module owns wire values only.
 */

export const ASPECT_RATIO_WIDE = '16:9';
export const ASPECT_RATIO_TALL = '9:16';
export const ASPECT_RATIO_SQUARE = '1:1';
export const ASPECT_RATIO_PORTRAIT = '4:5';
export const ASPECT_RATIO_OTHER = 'Other';

export const ASPECT_RATIO_NAMED_VALUES = [
  ASPECT_RATIO_WIDE,
  ASPECT_RATIO_TALL,
  ASPECT_RATIO_SQUARE,
  ASPECT_RATIO_PORTRAIT,
] as const;

export const ASPECT_RATIO_VALUES = [...ASPECT_RATIO_NAMED_VALUES, ASPECT_RATIO_OTHER] as const;

export type AspectRatioValue = (typeof ASPECT_RATIO_VALUES)[number];

/**
 * `core.raw_table_view.source` values that carry aspect-ratio data.
 * My Brand splits Google into these campaign types. `demand gen` and
 * `demand_gen` both title-case to "Demand Gen".
 */
export const GOOGLE_ASPECT_RATIO_SOURCES = [
  'demand gen',
  'demand_gen',
  'pmax',
  'video',
  'display',
] as const;

const GOOGLE_ASPECT_RATIO_SOURCE_SET = new Set<string>(GOOGLE_ASPECT_RATIO_SOURCES);

export function isGoogleAspectRatioSource(platform: string): boolean {
  return GOOGLE_ASPECT_RATIO_SOURCE_SET.has(platform.trim().toLowerCase());
}

const ASPECT_RATIO_VALUE_SET = new Set<string>(ASPECT_RATIO_VALUES);

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function isAllAspectRatios(values: string[]): boolean {
  if (values.length !== ASPECT_RATIO_VALUES.length) return false;
  return values.every((value) => ASPECT_RATIO_VALUE_SET.has(value))
    && new Set(values).size === ASPECT_RATIO_VALUES.length;
}

/**
 * Canonical BQ aspect-ratio list.
 * Empty / all five UI tokens => All (`[]`).
 */
export function toBqAspectRatio(values: string[]): string[] {
  const unique = uniquePreserveOrder(values.map((value) => value.trim()).filter((value) => value !== ''));
  if (unique.length === 0 || isAllAspectRatios(unique)) return [];
  return unique;
}
